import { Box, Typography } from '@mui/material';
import { AnimatePresence } from 'framer-motion';
import { AlertCircle } from 'lucide-react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { masks } from './utils';

import { ErrorContainer, ErrorMessage, InputWrapper, StyledInput, TinyLabel } from './style';

export interface InputProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<any, any>;
  id?: string;
  label?: string;
  placeholder?: string;
  maskType?: 'phone' | 'cpf' | 'money';
}

const inputTypeMap = {
  phone: 'tel',
  cpf: 'text',
  money: 'text',
} as const;

const Inputs = <T extends FieldValues>({ name, control, id, label, placeholder, maskType }: InputProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => {
        const hasError = !!error;
        const isFilled = !!value;

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const rawValue = e.target.value;

          if (maskType) {
            const maskedValue = masks[maskType](rawValue);
            onChange(maskedValue);
            return;
          }

          onChange(rawValue);
        };

        return (
          <Box display="flex" flexDirection="column" width="100%">
            <InputWrapper $hasError={hasError} $isFilled={isFilled}>
              {isFilled && label && <TinyLabel>{label}</TinyLabel>}

              <StyledInput
                id={id}
                type={maskType ? inputTypeMap[maskType] : 'text'}
                value={value || ''}
                onChange={handleChange}
                onBlur={onBlur}
                inputRef={ref}
                placeholder={isFilled ? '' : placeholder}
                aria-invalid={hasError}
              />
            </InputWrapper>

            <AnimatePresence>
              {hasError && (
                <ErrorContainer role="alert">
                  <AlertCircle size={16} />
                  <Typography component="span" sx={{ fontSize: '13px', lineHeight: '18px' }}>
                    <ErrorMessage>{error?.message}</ErrorMessage>
                  </Typography>
                </ErrorContainer>
              )}
            </AnimatePresence>
          </Box>
        );
      }}
    />
  );
};

export default Inputs;
